import { z } from "zod";

import type { CloudCruiseRunType } from "@/src/models/cloudcruise-run";

export const CommsEscalationReason = {
  INVALID_PHONE_NUMBER: "invalid_phone_number",
  CONVERSATION_AUTO_CLOSED: "conversation_auto_closed",
  WORKERS_COMP_REFERRAL: "workers_comp_referral",
} as const;

export const referralTelephoneEncounterSchema = z.object({
  reason: z.string(),
  message: z.string(),
  providerAssignment: z.string(),
});

export type ReferralTelephoneEncounter = z.infer<typeof referralTelephoneEncounterSchema>;

/** Recorded by a referral workflow's telephone-encounter step so later tasks can surface the same instructions. */
export const referralTelephoneEncountersOutputSchema = z.object({
  encounters: z.array(referralTelephoneEncounterSchema),
});

export const commsEscalationMetadataSchema = z.discriminatedUnion("reason", [
  z.object({
    reason: z.literal(CommsEscalationReason.INVALID_PHONE_NUMBER),
    phoneNumber: z.string().nullable(),
    attemptedAt: z.string().optional(),
    referralTelephoneEncounters: z.array(referralTelephoneEncounterSchema).optional(),
  }),
  z.object({
    reason: z.literal(CommsEscalationReason.CONVERSATION_AUTO_CLOSED),
    closingMessageId: z.string().uuid(),
    referralTelephoneEncounters: z.array(referralTelephoneEncounterSchema).optional(),
  }),
  z.object({
    reason: z.literal(CommsEscalationReason.WORKERS_COMP_REFERRAL),
    referralId: z.string().uuid(),
    referralTelephoneEncounters: z.array(referralTelephoneEncounterSchema).optional(),
  }),
]);

export type CommsEscalationMetadata = z.infer<typeof commsEscalationMetadataSchema>;

/**
 * Every reason shares one RPA workflow and one webhook, so the run type is the only thing telling
 * the triggers apart in `cloudcruise_run` — keep one per reason. Unset `note` takes the builder's default.
 */
export type CommsEscalationDispatch = {
  note?: string;
  runType: CloudCruiseRunType;
};

/**
 * State of the CloudCruise run that mirrors this escalation into ModMed.
 *
 * `in_progress` matters most: the escalation stays open for the minutes the RPA is running, and
 * the workflow does not check for an existing task before creating one, so a coordinator acting
 * on the card mid-run would put a second Schedule task in the call-center queue.
 *
 * `stalled` is a PROCESSING run old enough that its webhook is presumed lost — without it a run
 * that never reports back would hide the manual instructions forever.
 */
export const commsEscalationAutomationStates = ["none", "in_progress", "stalled", "succeeded", "failed"] as const;

export type CommsEscalationAutomation = (typeof commsEscalationAutomationStates)[number];

export const commsEscalationModMedInputsSchema = z.object({
  fields: z.array(z.object({ label: z.string(), value: z.string() })),
  /** Labels that could not be resolved, so the UI can say what to look up manually. */
  unresolved: z.array(z.string()),
  /** Set when this escalation has no ModMed task to create at all (e.g. an invalid phone number). */
  notApplicableReason: z.string().nullable(),
  automation: z.enum(commsEscalationAutomationStates),
});

export type CommsEscalationModMedInputs = z.infer<typeof commsEscalationModMedInputsSchema>;

export function getCommsEscalationNote(metadata: CommsEscalationMetadata): string {
  switch (metadata.reason) {
    case CommsEscalationReason.INVALID_PHONE_NUMBER:
      return "Initial outreach failed because the patient's phone number is ineligible for SMS (missing, invalid, or unable to receive SMS).";
    case CommsEscalationReason.WORKERS_COMP_REFERRAL:
      return "Outreach was not attempted because the referral is a workers' compensation case, which the clinic schedules itself.";
    default:
      return "The cron auto-sweep automatically closed out the conversation because the patient did not respond to outreach.";
  }
}
